<template>
    <div class="activity-tab-container" :aria-busy="pending">
        <p class="sr-only" role="status">{{ activityStatus }}</p>
        <div class="activity-header">
            <p class="activity-title">Recent Activity with {{ company?.name || 'Company' }}</p>
        </div>

        <SharedSpinner v-if="pending" label="Loading activity..." />

        <div v-else-if="conversation.length > 0" class="activity-content">
            <ActivityFeed :activities="conversation" :loading="pending" :exposure-status="exposure?.status" />
        </div>

        <div v-else-if="activityPurged" class="no-activity">
            <p class="no-activity-title">{{ PURGED_ACTIVITY_TITLE }}</p>
            <p class="no-activity-message">{{ PURGED_ACTIVITY_MESSAGE }}</p>
        </div>

        <div v-else class="no-activity">
            <p class="text-gray-500 text-center py-8">No Activity Found</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import ActivityFeed from '~/components/shared/ActivityFeed.vue';
import { useCompanyActivity } from '~/composables/useCompanyActivity';
import { useExposureSidebarStore } from '~/stores/components/modals/exposure-sidebar';
import { isActivityPurged, PURGED_ACTIVITY_MESSAGE, PURGED_ACTIVITY_TITLE } from '~/utils/activity-retention';

const exposureStore = useExposureSidebarStore();
const { exposure, company } = storeToRefs(exposureStore);

const { conversation, pending, loadFailed, activityCountLabel } = useCompanyActivity(
    computed(() => company.value?.id),
    { watchSources: [() => exposureStore.selectedExposure] },
);

// A failed fetch also empties the list, and that is not expired history.
const activityPurged = computed((): boolean => !loadFailed.value && isActivityPurged(exposure.value?.last_seen_at));

const activityStatus = computed((): string => {
    if (pending.value) return 'Loading activity';
    // A failed fetch empties the list, and "no activity" is a different claim from "we could
    // not ask". Nothing else on this tab reports the failure, so the region has to.
    if (loadFailed.value) return 'Activity could not be loaded';
    if (!conversation.value.length) return activityPurged.value ? PURGED_ACTIVITY_TITLE : 'No activity found';
    return activityCountLabel.value;
});
</script>

<style lang="scss" scoped>
.activity-tab-container {
    @apply flex flex-col;
}

.activity-header {
    @apply px-6 py-5;
}

.activity-title {
    @apply text-zinc-950 text-base font-semibold leading-7;
}

.activity-content {
    @apply pb-4;
}

.no-activity {
    @apply text-center py-8;
}

.no-activity-title {
    @apply text-base font-semibold text-gray-900 leading-6;
}

.no-activity-message {
    @apply mx-auto max-w-sm px-6 pt-1 text-sm text-gray-600 leading-5;
}
</style>

